import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "../../components/Seo";
import StatusBadge from "../../components/StatusBadge";
import { LoadingState, ErrorState, EmptyState } from "../../components/StateBlocks";
import useAuth from "../../hooks/useAuth";
import api, { ApiError } from "../../utils/api";

export default function AdminEventDetail() {
  const { eventId } = useParams();
  const { idToken } = useAuth();
  const [event, setEvent] = useState(null);
  const [rsvps, setRsvps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [actionError, setActionError] = useState(null);

  function load() {
    setLoading(true);
    Promise.all([api.adminGetEvent(idToken, eventId), api.adminListRsvps(idToken, eventId)])
      .then(([eventData, rsvpData]) => {
        setEvent(eventData);
        setRsvps(rsvpData.rsvps || []);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : "Unable to load event details."))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, idToken]);

  const filteredRsvps = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rsvps;
    return rsvps.filter(
      (r) =>
        r.parentFirstName?.toLowerCase().includes(term) ||
        r.parentEmail?.toLowerCase().includes(term) ||
        r.parentLastInitial?.toLowerCase().includes(term),
    );
  }, [rsvps, search]);

  async function handleCancelRsvp(rsvpId) {
    setActionError(null);
    try {
      await api.adminUpdateRsvp(idToken, eventId, rsvpId, { status: "CANCELED" });
      load();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : "Unable to cancel this RSVP.");
    }
  }

  if (loading) return <LoadingState label="Loading event\u2026" />;
  if (error) return <ErrorState message={error} />;
  if (!event) return null;

  return (
    <>
      <Seo title={`Admin: ${event.title}`} />
      <StatusBadge status={event.status} />
      <h1>{event.title}</h1>
      <p>
        <Link to={`/admin/events/${eventId}/edit`}>Edit event details</Link>
      </p>
      <p>
        {event.confirmedTeenCount} of {event.capacity} confirmed &middot; {event.waitlistCount} waitlisted
      </p>

      {actionError && (
        <div className="alert alert-error" role="alert">
          {actionError}
        </div>
      )}

      <div className="form-field">
        <label htmlFor="rsvp-search">Search RSVPs</label>
        <input
          id="rsvp-search"
          type="text"
          placeholder="Search by name or email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {filteredRsvps.length === 0 ? (
        <EmptyState title="No RSVPs match" />
      ) : (
        <table className="data-table">
          <caption className="visually-hidden">RSVPs for {event.title}</caption>
          <thead>
            <tr>
              <th scope="col">Parent</th>
              <th scope="col">Email</th>
              <th scope="col">Teens</th>
              <th scope="col">Status</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredRsvps.map((r) => (
              <tr key={r.rsvpId}>
                <td>
                  {r.parentFirstName} {r.parentLastInitial}
                </td>
                <td>{r.parentEmail}</td>
                <td>{r.teenCount}</td>
                <td>
                  <StatusBadge status={r.status} />
                </td>
                <td>
                  {(r.status === "CONFIRMED" || r.status === "WAITLISTED") && (
                    <button type="button" className="btn btn-danger" onClick={() => handleCancelRsvp(r.rsvpId)}>
                      Cancel RSVP
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}
